import { Dispatch, SetStateAction } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import Modal from "../../layouts/Modal";
import { IPreviousJobPartList, PreviousJobPartListMatchType } from "../../models/Job";
import { CategoryWithParts, IPartCategory } from "../../models/Part";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";

export const getPreviousPartListMatchLabel = (matchType: PreviousJobPartListMatchType) => {
  if (matchType === PreviousJobPartListMatchType.KType) return "KType";
  if (matchType === PreviousJobPartListMatchType.MakeModelYear) return "make, model and year";

  return "make, model and year range";
};

const buildPreviousPartListOptionLabel = (item: IPreviousJobPartList, unknownVrmLabel: string) => {
  const vrm = item.vrm?.trim() || unknownVrmLabel;
  const makeModel = item.makeModelDescription?.trim();
  const year = item.yearOfManufacture ? ` (${item.yearOfManufacture})` : "";
  const created = item.created ? ` – ${formatDateTime(item.created, DateTime.DATE_SHORT)}` : "";
  const suffix = makeModel ? ` – ${makeModel}${year}` : year;

  return `${vrm}${suffix}${created}`;
};

interface PreviousPartListModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  previousPartListData: IPreviousJobPartList | null;
  previousPartListsData: IPreviousJobPartList[];
  selectedPreviousJobId: string | null;
  setSelectedPreviousJobId: Dispatch<SetStateAction<string | null>>;
  categories: CategoryWithParts[];
  allCategories: IPartCategory[] | undefined;
  onApply: () => void;
}

export default function PreviousPartListModal({
  open,
  setOpen,
  previousPartListData,
  previousPartListsData,
  selectedPreviousJobId,
  setSelectedPreviousJobId,
  categories,
  allCategories,
  onApply,
}: PreviousPartListModalProps) {
  const { t } = useTranslation("jobs");
  const getPreviousPartListCategory = (partCategoryId: string) => {
    const existingCategory = categories?.find(c => c.category.id === partCategoryId);
    const masterCategory = allCategories?.find(c => c.id === partCategoryId);

    return {
      existingCategory,
      category: existingCategory?.category ?? masterCategory,
    };
  };

  const hasMultiple = previousPartListsData.length > 1;
  const activeJobId = selectedPreviousJobId ?? previousPartListData?.jobId ?? "";

  return (
    <Modal open={open} setOpen={setOpen} width={"max-w-4xl"}>
      <div className="space-y-5">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t("Previous Part List")}</h3>
          {previousPartListData && (
            <p className="mt-1 text-sm text-gray-500">
              {t("Matched by {{matchType}}", { matchType: t(getPreviousPartListMatchLabel(previousPartListData.matchType)) })}
              {previousPartListData.jobTitle ? ` ${t("from {{title}}", { title: previousPartListData.jobTitle })}` : ""}
              {previousPartListData.created ? ` ${t("on {{date}}", { date: formatDateTime(previousPartListData.created, DateTime.DATETIME_SHORT) })}` : ""}.
            </p>
          )}
        </div>

        {hasMultiple && (
          <div>
            <label htmlFor="previous-part-list-vrm" className="block text-sm font-medium text-gray-700">
              {t("Historical part list")}
            </label>
            <select
              id="previous-part-list-vrm"
              className="mt-1 block w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
              value={activeJobId}
              onChange={(e) => setSelectedPreviousJobId(e.target.value)}
            >
              {previousPartListsData.map(item => (
                <option key={item.jobId} value={item.jobId}>
                  {buildPreviousPartListOptionLabel(item, t("Unknown VRM"))}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {previousPartListsData.length === 1
                ? t("Showing {{count}} matching previous part list.", { count: previousPartListsData.length })
                : t("Showing {{count}} matching previous part lists.", { count: previousPartListsData.length })}
            </p>
          </div>
        )}

        <div className="max-h-[520px] overflow-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{t("Category")}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{t("Price chosen")}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{t("Last sold price")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {previousPartListData?.categories.map(previousCategory => {
                const { existingCategory, category } = getPreviousPartListCategory(previousCategory.partCategoryId);
                const price = previousCategory.price ?? existingCategory?.price ?? null;
                const lastSoldPrice = previousCategory.lastSoldPrice ?? existingCategory?.lastSoldPrice ?? null;
                const lastSoldDate = existingCategory?.lastSoldDate ?? null;

                return (
                  <tr key={previousCategory.partCategoryId}>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {category?.name ?? t("Unknown category")}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {price != null ? PriceFormat(price) : t("No price")}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {lastSoldPrice != null
                        ? (
                          <>
                            {PriceFormat(lastSoldPrice)}
                            {lastSoldDate ? <span className="text-xs"> ({formatDateTime(lastSoldDate, DateTime.DATE_SHORT)})</span> : ""}
                          </>
                        )
                        : t("No history")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            onClick={() => setOpen(false)}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
            onClick={onApply}
          >
            {t("Map onto current list")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
